import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

import classNames from 'classnames';

import CircleButton from '@src/components/Buttons/CircleButton';

import styles from './styles.module.less';

export type SlidePanelContextValue = {
  isOpen: boolean;
  toggle: () => void;
};

const SlidePanelContextInternal = createContext<SlidePanelContextValue | undefined>(undefined);

export function useSlidePanel(): SlidePanelContextValue {
  const ctx = useContext(SlidePanelContextInternal);
  if (ctx === undefined) {
    throw new Error('useSlidePanel must be used within SlidePanel');
  }
  return ctx;
}

export type SlidePanelProps = {
  children: ReactNode;
  title?: ReactNode;
  defaultOpen?: boolean;
  collapsedWidth?: number;
  expandedWidth?: number;
  className?: string;
};

function SlidePanel({
  children,
  title,
  defaultOpen = true,
  collapsedWidth = 52,
  expandedWidth = 200,
  className,
}: SlidePanelProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggle = () => setIsOpen(prev => !prev);

  const value = useMemo(() => ({ isOpen, toggle }), [isOpen]);

  return (
    <SlidePanelContextInternal.Provider value={value}>
      <div
        className={classNames(styles.root, { [styles.collapsed]: !isOpen }, className)}
        style={{
          width: isOpen ? expandedWidth : collapsedWidth,
          minWidth: isOpen ? expandedWidth : collapsedWidth,
        }}
      >
        {isOpen ? (
          <div className={styles.expandedShell}>
            <div className={styles.header}>
              <span className={styles.headerTitle}>{title}</span>
              <button className={styles.toggleButton} onClick={toggle} type="button">
                <span className="material-symbols-outlined gx-text-primary">right_panel_open</span>
              </button>
            </div>
            <div className={styles.body}>{children}</div>
          </div>
        ) : (
          <div className={styles.collapsedShell}>
            <div className={styles.toggleCollapsed}>
              <button className={styles.toggleButton} onClick={toggle} type="button">
                <span className="material-symbols-outlined">left_panel_open</span>
              </button>
            </div>
            {children}
          </div>
        )}
      </div>
    </SlidePanelContextInternal.Provider>
  );
}

export default SlidePanel;
